import { motion, useAnimate } from 'framer-motion'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import type { LiveGiftsAnimations } from '../../../../types/lives/animations'

interface Props {
  animation: LiveGiftsAnimations
  removeAnimation: (id: string) => void
}

const INITIAL_X = window.innerWidth / 2.5
const INITIAL_Y = window.innerHeight / 4
// origem do HOST (dono da sala): o avatar dele fica no TOPO-ESQUERDO (header). Ponto
// SINTÉTICO e TUNÁVEL — a medição da âncora 'streamer' vinha instável (o presente
// saía à direita/invertido), então fixo aqui. Frações da largura escalam entre
// devices. HOST_ORIGIN_X_FRAC ↓ = mais à ESQUERDA; HOST_ORIGIN_Y ↓ = mais pra CIMA.
const HOST_ORIGIN_X_FRAC = 0.13
const HOST_ORIGIN_Y = 44
const HOST_CENTER_X = window.innerWidth * HOST_ORIGIN_X_FRAC
const HOST_X = HOST_CENTER_X - 48 // -48 centraliza o lead (w-24 = 96px) no ponto
const HOST_Y = HOST_ORIGIN_Y - 48
// "um pouco acima" do centro vertical (pedido do produto): o presente grande para
// numa fração da altura ACIMA do meio, pra não cobrir os controles de baixo e ficar
// mais no campo de visão. ↑ = mais alto. 0 = centro exato da tela.
const CENTER_Y_LIFT = 0.1
// nudge horizontal fino do centro. O centro geométrico já é innerWidth/2 - 48 (48 =
// metade do lead w-24; o zoom escala a partir do centro do elemento), então 0 =
// centro exato. Porém as artes dos gifts vêm levemente deslocadas à DIREITA no frame
// (padding assimétrico do PNG), então o presente parava um pouco à direita do centro.
// -16 compensava — mas na prática deixava o presente PUXADO PRA ESQUERDA
// (report QA #62). 0 = centro geométrico exato.
const CENTER_X_NUDGE = 0

// tempos (segundos) — MEDIDOS do vídeo de referência (30fps). Tunar ao vivo (HMR).
// subida remetente -> centro: duração PROPORCIONAL à distância (px/s), então o
// HOST (no topo, longe) sobe mais DEVAGAR e um guest perto sobe rápido — velocidade
// visual consistente. ↓ RISE_SPEED = tudo mais devagar subindo.
const RISE_SPEED = 340 // px/s (subida remetente -> centro; ↓ = mais devagar)
const RISE_MIN = 0.48 // piso da subida (s)
const RISE_MAX = 0.9 // teto da subida (s)
// zoom limitado pela LARGURA da tela: 3 é o teto, mas o lead (96px) escalado
// nunca passa de ~55% da tela — em aparelho estreito o presente estourava as
// bordas (report QA #61)
const ZOOM_SCALE = Math.min(3, (window.innerWidth * 0.55) / 96)
const ZOOM_IN = 0.14 // cresce no centro
const CENTER_HOLD = 0.6 // SEGURA o presente grande no centro antes de espalhar (↑ = espera mais)
const ZOOM_OUT = 0.1 // volta ao tamanho antes de espalhar
const FAN_OUT = 0.34 // centro -> cada seat (TODAS juntas; ↑ = vai mais devagar)
const FAN_HOLD = 0.12 // SEGURA no seat (pra VER que o presente chegou)
const FAN_FADE = 0.18 // some depois de chegar no seat
const ANCHOR_MAX_MS = 250

// spray/leque: cada seat recebe VÁRIAS cópias do presente que explodem do centro
// com rotação/escala/offset próprios (igual o vídeo: um monte de megafones
// jorrando pras seats). Todas partem quase juntas (delay ~0) → "ao mesmo tempo".
const PARTICLES_PER_SEAT = 1 // cópias por seat (1 = um presente por cadeira)
const SCATTER = 0 // dispersão (px) ao redor da seat (0 = pousa no centro da seat)
const ROT_MAX = 0 // rotação máx (graus). 0 = presente sempre no ângulo natural
const BURST_DELAY = 0.05 // atraso máx entre cópias (pequeno = quase junto)

// POUSO na seat: a âncora já é centralizada no CENTRO do avatar (translate -50% na
// index.tsx), então bias 0 = presente no centro exato do avatar. Ajuste fino em px:
// LAND_Y_BIAS + empurra pra BAIXO / - pra CIMA; LAND_X_BIAS + direita / - esquerda.
// LAND_SCALE menor = presente cabe melhor dentro do avatar. "vamos testando" aqui.
const LAND_SCALE = 0.78
const LAND_X_BIAS = 0
const LAND_Y_BIAS = 20

// pseudo-random ESTÁVEL por seed (mesmo valor a cada render → não fica tremendo)
const rand = (seed: number) => {
  const x = Math.sin(seed * 99.13) * 43758.5453
  return x - Math.floor(x)
}
const rrange = (seed: number, min: number, max: number) =>
  min + rand(seed) * (max - min)

interface Particle {
  id: string
  x: number
  y: number
  rotate: number
  scale: number
  delay: number
}

// destino sintético do host (mesmo ponto topo-esquerdo da origem), centralizado no
// HOST_CENTER. Usado pro 'streamer', cuja âncora de header vinha instável.
const HOST_RECT = {
  x: HOST_CENTER_X - 40,
  y: HOST_ORIGIN_Y - 40,
  left: HOST_CENTER_X - 40,
  top: HOST_ORIGIN_Y - 40,
  width: 80,
  height: 80,
  right: HOST_CENTER_X + 40,
  bottom: HOST_ORIGIN_Y + 40,
} as DOMRect

function captureRects(ids: string[]): Promise<(DOMRect | null)[]> {
  return new Promise((resolve) => {
    const start = Date.now()
    const tick = () => {
      const rects = ids.map((id) => {
        // host ('streamer') SEMPRE no ponto sintético (âncora de header instável)
        if (id === 'streamer') return HOST_RECT
        const el = document.getElementById(id)
        return el ? el.getBoundingClientRect() : null
      })
      if (rects.every(Boolean) || Date.now() - start > ANCHOR_MAX_MS) {
        return resolve(rects)
      }
      requestAnimationFrame(tick)
    }
    tick()
  })
}

// Uma cópia (partícula) do presente que EXPLODE do centro até perto de UMA seat,
// com rotação/escala próprias e um rabinho de fade. Todas montam juntas → jorram
// AO MESMO TEMPO (o delay minúsculo por cópia dá o ar de "spray" orgânico).
const GiftParticle = memo(
  ({
    p,
    imgGift,
    origin,
  }: {
    p: Particle
    imgGift: string
    origin: { x: number; y: number }
  }) => {
    const [scope, animate] = useAnimate()

    useEffect(() => {
      if (!scope.current) return
      animate([
        [scope.current, { opacity: 1 }, { duration: 0.01, delay: p.delay }],
        [
          scope.current,
          { x: p.x, y: p.y, rotate: p.rotate, scale: p.scale },
          { duration: FAN_OUT, ease: [0.2, 0.7, 0.3, 1] },
        ],
        // segura no seat (FAN_HOLD) e só então some → dá pra VER que chegou
        [
          scope.current,
          { opacity: 0, scale: p.scale * 0.7 },
          { duration: FAN_FADE, delay: FAN_HOLD },
        ],
      ])
    }, [p, scope, animate])

    return (
      <motion.div
        ref={scope}
        className="absolute w-20 h-20 z-10"
        // parte do CENTRO da animação (centro da tela, via state `center`), não de ponto fixo
        style={{ x: origin.x, y: origin.y, opacity: 0, rotate: 0, scale: 1 }}
      >
        <img src={imgGift} className="w-full h-full" />
      </motion.div>
    )
  },
)

export const Animation = memo(({ animation, removeAnimation }: Props) => {
  const [scope, animate] = useAnimate()
  const [rects, setRects] = useState<(DOMRect | null)[] | null>(null)
  // centro da animação = CENTRO REAL DA TELA (innerWidth/2), calculado no run().
  // (Já foi centróide das seats, mas mandando p/ 1 só subia pro user; ver run().)
  const [center, setCenter] = useState({ x: INITIAL_X, y: INITIAL_Y })

  // memoizado: array estável, senão `run` recriaria a cada render e o efeito
  // ficaria re-disparando a animação (zoom repetindo, fan-out nunca estabiliza).
  const elements = useMemo(
    () => (animation.elementsToGo ?? []).map((e) => String(e)),
    [animation.elementsToGo],
  )

  const run = useCallback(async () => {
    if (!scope.current || !animation.id) return

    // captura as âncoras das seats JÁ no início (reusadas no leque depois).
    const captured = await captureRects(elements)
    // CENTRO = centro REAL da tela (innerWidth/2), SEMPRE — independe de quantos/
    // quais destinatários. Antes usava o centróide das seats capturadas, mas aí
    // mandando pra 1 só o "centro" virava a posição DAQUELE user (subia pro user, não
    // ao meio da tela); e pra "todos" ficava torto se os seats eram assimétricos. O
    // lead sobe ao meio da tela, segura/dá zoom, e SÓ ENTÃO espalha pras seats.
    // -48 ≈ metade do lead (w-24) p/ centralizar a arte (o zoom escala do centro).
    const centerX = window.innerWidth / 2 - 48 + CENTER_X_NUDGE
    // vertical fica no campo aberto ACIMA das seats (CENTER_Y_LIFT), não no centróide.
    const centerY = window.innerHeight * (0.5 - CENTER_Y_LIFT) - 48

    // origem: âncora REAL de quem enviou. Host = âncora 'streamer' (posição REAL do
    // avatar do dono, enviada pelo Follow); senão a seat do remetente. Fallback pro
    // topo-centro sintético (HOST_X/Y) só se a âncora ainda não foi medida.
    const isStreamer =
      animation.elementFrom == null ||
      String(animation.elementFrom) === 'streamer'
    // HOST → ponto sintético topo-esquerdo (HOST_X/Y). Guest sentado → âncora REAL da
    // sua seat (centralizada no avatar). Sem âncora → centro da tela (fallback).
    const fromEl = !isStreamer
      ? document.getElementById(String(animation.elementFrom))
      : null
    const from = fromEl?.getBoundingClientRect()
    // centraliza o lead (w-24=96px) na âncora (já centralizada no avatar via translate)
    const startX = isStreamer ? HOST_X : from ? from.x + from.width / 2 - 48 : centerX
    const startY = isStreamer ? HOST_Y : from ? from.y + from.height / 2 - 48 : centerY

    // GIFT COM MP4: o Zego (nativo) renderiza o efeito grande no centro. Aqui a webview
    // NÃO faz a subida/zoom no centro (senão vira DUAS animações sobrepostas) — só leva
    // a imagem da seat do REMETENTE direto às seats destino (seat -> seat). O leque
    // parte da seat do remetente (origin = start), sem lead grande no meio.
    if (animation.animationMediaGift) {
      setCenter({ x: startX, y: startY })
      await animate(scope.current, { x: startX, y: startY, scale: 1, opacity: 0 }, { duration: 0 })
      setRects(captured)
      return
    }

    // GIFT PNG (sem mp4): animação COMPLETA — sobe ao centro, dá zoom, segura e espalha.
    setCenter({ x: centerX, y: centerY })
    await animate(scope.current, { x: startX, y: startY, scale: 1, opacity: 1 }, { duration: 0 })

    // subida proporcional à distância → host (lá em cima, longe) sobe mais devagar
    const dist = Math.hypot(centerX - startX, centerY - startY)
    const riseDur = Math.min(RISE_MAX, Math.max(RISE_MIN, dist / RISE_SPEED))
    await animate(scope.current, { x: centerX, y: centerY }, { duration: riseDur, ease: 'easeInOut' })
    await animate(scope.current, { scale: ZOOM_SCALE }, { duration: ZOOM_IN, ease: 'easeOut' })
    // segura o presente GRANDE no centro por um instante antes de espalhar pras seats
    await new Promise((r) => setTimeout(r, CENTER_HOLD * 1000))
    await animate(scope.current, { scale: 1 }, { duration: ZOOM_OUT, ease: 'easeIn' })

    // solta o leque JUNTO (reusa as âncoras já capturadas no início)
    await animate(scope.current, { opacity: 0 }, { duration: 0.08 }) // some o lead
    setRects(captured)
  }, [scope, animate, animation, elements])

  useEffect(() => {
    if (scope.current) run()
  }, [run, scope])

  // partículas do spray: várias cópias por seat capturada, cada uma com
  // dispersão/rotação/escala próprias. useMemo p/ não re-sortear a cada render.
  const particles = useMemo<Particle[]>(() => {
    if (!rects) return []
    const out: Particle[] = []
    rects.forEach((rect, i) => {
      if (!rect) return
      for (let k = 0; k < PARTICLES_PER_SEAT; k++) {
        const s = i * 7.3 + k * 2.1 + 1
        out.push({
          id: `${i}-${k}`,
          // pousa no CENTRO do avatar: rect.center (âncora centralizada no avatar) -
          // metade da partícula (w-20=80px -> -40) + bias fino ajustável.
          x: rect.x + rect.width / 2 - 40 + LAND_X_BIAS + rrange(s, -SCATTER, SCATTER),
          y: rect.y + rect.height / 2 - 40 + LAND_Y_BIAS + rrange(s + 0.5, -SCATTER, SCATTER),
          rotate: rrange(s + 1, -ROT_MAX, ROT_MAX),
          scale: LAND_SCALE, // menor ao chegar, pra caber dentro do avatar da seat
          delay: rrange(s + 3, 0, BURST_DELAY),
        })
      }
    })
    return out
  }, [rects])

  useEffect(() => {
    if (!rects || !animation.id) return
    const timer = setTimeout(
      () => removeAnimation(animation.id as string),
      (FAN_OUT + BURST_DELAY + FAN_HOLD + FAN_FADE) * 1000 + 200,
    )
    return () => clearTimeout(timer)
  }, [rects, animation.id, removeAnimation])

  return (
    <div>
      {/* lead: remetente -> centro -> zoom (o ÚNICO no centro) */}
      <motion.div
        ref={scope}
        className="absolute w-24 h-24 z-10"
        style={{ x: INITIAL_X, y: INITIAL_Y, opacity: 0 }}
      >
        <img src={animation.imgGift} className="w-full h-full" />
      </motion.div>

      {/* leque/spray: várias cópias por seat explodem do centro AO MESMO TEMPO */}
      {rects &&
        particles.map((p) => (
          <GiftParticle
            key={`${animation.id}-${p.id}`}
            p={p}
            imgGift={animation.imgGift}
            origin={center}
          />
        ))}
    </div>
  )
})

GiftParticle.displayName = 'GiftParticle'
Animation.displayName = 'Animation'
